import { useState, useRef, useCallback, useEffect } from 'react'

import { onboardingSessionKey } from '../context/OnboardingContext.jsx'

import {
  wrapFrameIndex,
  shortestRotationPath,
} from '../../hero/hooks/useModelFrames.js'
import { getAxonometricFrameUrl } from '../../../shared/utils/unitAssets.js'

import { MODEL_FRAME_INTERVAL_MS } from '../../hero/constants/animationTimings.js'
import { PRODUCT_VIEW_MODE } from '../constants/viewModes.js'

// ── Constants ─────────────────────────────────────────────────────────────────

/** Minimum ms between frames for go-to-north animated step (~30 fps). */
const ANIM_INTERVAL_MS = MODEL_FRAME_INTERVAL_MS

/**
 * Slower interval for the intro animation — deliberate enough for users to
 * read it as "this model is rotatable". ~5 fps at 200ms per step.
 * Full loop (framesCount=8 frames): 8 × 200 ms = 1600 ms ≈ 1.6 s.
 */
const INTRO_FRAME_MS = 200

/**
 * Total pixels for a full 360° drag rotation.
 * Identical to ModelView (72 frames × 8 px/frame = 576 px) so dragging feels
 * the same regardless of frame count — fewer frames = fewer steps per drag.
 */
const FULL_ROTATION_PX = 576

/**
 * axonometricNorthFrameIndex — returns the frame index where the camera
 * faces geographic north.
 *
 * Generator convention: frame 0 (file _001) = camera to the geographic NORTH,
 * pointing SOUTH. Camera orbits CW (viewed from above) as index increases.
 * Therefore geographic-north-facing = index framesCount/2 (camera at the south
 * of the building, pointing north).
 *
 * This is independent of product.azimuth. Azimuth affects the "attractive
 * view" frame selection (see axonometricAttractiveFrameIndex); go-to-north
 * always targets framesCount/2.
 *
 * @param {number} framesCount
 * @returns {number}
 */
export function axonometricNorthFrameIndex(framesCount) {
  return Math.floor(framesCount / 2)
}

/**
 * axonometricAttractiveFrameIndex — returns the frame index that shows the
 * building from its most architecturally interesting angle.
 *
 * Finds the frame closest
 * to the building's azimuth direction (the direction the building's north
 * faces in geographic space). In most projects this is the frame that shows
 * the building's primary (south-exposed) facade from the front.
 *
 * @param {number} framesCount
 * @param {number} azimuth   — product.azimuth (CCW degrees from +X to geographic north)
 * @returns {number}
 */
export function axonometricAttractiveFrameIndex(framesCount, azimuth) { //TODO: REDO THIS!
  if (!framesCount) return 0
  const az = Number.isFinite(Number(azimuth)) ? Number(azimuth) : 90
  const frameStep = 360 / framesCount
  return Math.round(az / frameStep) % framesCount
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * useAxonometricFrames — manages 360° render frame rotation for PlanRendererAxonometricMode.
 *
 * Modelled after `useModelFrames` in the hero domain.  Key differences:
 *   - No SVG groups (no `frameGroupsRef` / `activeSvgClass`).
 *   - Frame URL is built from product UUID + refTier (no HERO_VIEW concept).
 *   - `imageSetKey` = `{uuid}_{refTier}` — resets when product or level changes.
 *   - `compassAngle` is exposed as React state (8 frames → infrequent updates,
 *     no hot-path penalty) to drive `CardinalsOverlay` reactively.
   *   - Intro animation: fires once per session per axonometric mode visit;
 *     controlled by the shared `bim:onboarding_seen:axonometric` key (value '1' = skip).
 *
 * @param {object}      params
 * @param {string|null} params.projectDataUrl — project base asset URL
 * @param {object|null} params.product        — product (needs uuid + id_cad)
 * @param {number|null} params.refTier        — active floor/tier number
 * @param {number}      params.framesCount    — total frames (e.g. 8)
 * @param {number}      [params.azimuth=90]   — degrees from frame 0 to geographic north, CCW
 * @param {string}      params.activeFrameClass — CSS class toggled on the active `<img>`
 *
 * @returns {{
 *   frameImgRefsRef:   React.MutableRefObject<HTMLImageElement[]>,
 *   currentIndexRef:   React.MutableRefObject<number>,
 *   isDraggingRef:     React.MutableRefObject<boolean>,
 *   getFrameUrl:       (index: number) => string|null,
 *   loadingProgress:   number,
 *   isLoaded:          boolean,
 *   compassAngle:      number,
 *   onImgLoad:         (e: Event) => void,
 *   handleFrameChange: (index: number) => void,
 *   handleMouseDown:   (e: MouseEvent) => void,
 *   handleMouseMove:   (e: MouseEvent) => void,
 *   handleMouseUp:     () => void,
 *   handleTouchStart:  (e: TouchEvent) => void,
 *   handleTouchMove:   (e: TouchEvent) => void,
 *   handleTouchEnd:    () => void,
 *   handleRotateLeft:  () => void,
 *   handleRotateRight: () => void,
 *   handleGoToNorth:   () => void,
 * }}
 */
export function useAxonometricFrames({
  projectDataUrl,
  product,
  refTier,
  framesCount,
  azimuth = 90,
  activeFrameClass,
  compassElRef = null,
  attractiveFrameRef = null,
}) {

  // Adaptive drag sensitivity: same total drag distance = full rotation,
  // regardless of how many frames the render has.
  const pixelsPerFrame = Math.max(16, Math.round(FULL_ROTATION_PX / framesCount))

  // ── Refs (hot path — zero re-renders) ─────────────────────────────────────

  const frameImgRefsRef = useRef([])

  const currentIndexRef  = useRef(0)
  const isDraggingRef    = useRef(false)
  const dragStartXRef    = useRef(0)
  const dragBaseFrameRef = useRef(0)
  const loadedCountRef   = useRef(0)
  const isLoadedRef      = useRef(false)

  // ── State ─────────────────────────────────────────────────────────────────

  const [loadingProgress, setLoadingProgress] = useState(0)
  const [isLoaded,        setIsLoaded]        = useState(false)

  // compassAzimuthDeg feeds CardinalsOverlay as `azimuthDeg`.
  //
  // CardinalsOverlay computes: svgRotationDeg = -(azimuthDeg - 90)
  // We need svgRotationDeg = 0 when camera faces north (N at top of screen),
  // which happens at frame index = framesCount/2 (camera from south, pointing north).
  //
  // Derivation for frame i when camera orbits CW (frame 0 = camera at geographic north):
  //   compassAzimuthDeg = (270 + i × 360/framesCount) % 360
  //
  // Verification:
  //   frame 0 (camera at N, looks S) → 270 → svgRot=-180 → N at bottom  ✓
  //   frame 4 (camera at S, looks N) → 90  → svgRot=0   → N at top     ✓
  //   frame 2 (camera at E, looks W) → 0   → svgRot=90  → N at right   ✓
  //   frame 6 (camera at W, looks E) → 180 → svgRot=-90 → N at left    ✓
  const [compassAzimuthDeg, setCompassAzimuthDeg] = useState(270)

  // ── Image set key (determines which images are loaded) ────────────────────
  //
  // Changes when product or active tier changes — triggers both effects below.

  const imageSetKey = `${product?.uuid ?? ''}_${refTier ?? ''}`

  // ── Compass update ────────────────────────────────────────────────────────

  const updateCompass = useCallback((index) => {
    // Camera orbits CCW (from above) as index increases — drag right decreases index.
    // frame 0 = camera at geographic North, looking South → N at bottom (azimuth 270).
    // frame 4 = camera at South, looking North → N at top   (azimuth 90). ✓
    //
    // Derivation: camera right vector in (E, N) coords = (-cos β, sin β) where
    // bearing β = (360 - i×step) % 360. Projecting geographic North onto that
    // camera basis gives azimuthDeg = (270 - i×(360/framesCount) + 360) % 360.
    const deg = ((270 - index * (360 / framesCount)) % 360 + 360) % 360
    setCompassAzimuthDeg(deg)
    // Also drive the toolbar CompassNorth icon directly via DOM ref to avoid
    // React re-renders on each frame change (same pattern as ModelControls).
    // The needle points to geographic North on screen, which requires the same
    // rotation as CardinalsOverlay's svgRotationDeg = -(azimuth - 90) = 90 - deg.
    if (compassElRef?.current) {
      compassElRef.current.style.transform = `rotate(${90 - deg}deg)`
    }
  }, [framesCount])

  // ── Core: hot-path frame change ───────────────────────────────────────────

  const handleFrameChange = useCallback((newIndex) => {
    const prev = currentIndexRef.current
    if (prev === newIndex) return

    frameImgRefsRef.current[prev]?.classList.remove(activeFrameClass)
    frameImgRefsRef.current[newIndex]?.classList.add(activeFrameClass)

    currentIndexRef.current = newIndex
    updateCompass(newIndex)
  }, [activeFrameClass, updateCompass])

  // ── Effect 1 — apply CSS classes when image set changes ───────────────────
  //
  // Resets to frame 0 on product/tier change (new product = fresh start).
  // frameImgRefsRef points to the new DOM elements after React reconciles.

  useEffect(() => {
    currentIndexRef.current = 0
    dragBaseFrameRef.current = 0

    frameImgRefsRef.current.forEach((el, i) => {
      el?.classList.toggle(activeFrameClass, i === 0)
    })

    updateCompass(0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSetKey, framesCount, activeFrameClass])

  // ── Effect 2 — loading reset (fires only when image content changes) ──────

  useEffect(() => {
    loadedCountRef.current = 0
    isLoadedRef.current    = false
    setLoadingProgress(0)
    setIsLoaded(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSetKey])

  // ── Loading ───────────────────────────────────────────────────────────────

  const onImgLoad = useCallback((e) => {
    const img = e.currentTarget
    const decodePromise = typeof img.decode === 'function'
      ? img.decode().catch(() => {})
      : Promise.resolve()

    decodePromise.then(() => {
      loadedCountRef.current += 1
      const progress = Math.round(loadedCountRef.current / framesCount * 100)
      setLoadingProgress(progress)
      if (loadedCountRef.current >= framesCount) {
        isLoadedRef.current = true
        setIsLoaded(true)
      }
    })
  }, [framesCount])

  // ── URL builder ───────────────────────────────────────────────────────────

  const getFrameUrl = useCallback((index) => {
    if (!projectDataUrl || !product || refTier == null) return null
    return getAxonometricFrameUrl(projectDataUrl, product, refTier, index)
  }, [projectDataUrl, product, refTier])

  // ── Drag — mouse ──────────────────────────────────────────────────────────

  const handleMouseDown = useCallback((e) => {
    if (!isLoadedRef.current) return
    cancelAnimationFrame(animRafRef.current)
    isDraggingRef.current    = true
    dragStartXRef.current    = e.clientX
    dragBaseFrameRef.current = currentIndexRef.current
  }, [])

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current) return
    const deltaX     = e.clientX - dragStartXRef.current
    const frameDelta = Math.round(deltaX / pixelsPerFrame)
    const newIndex   = wrapFrameIndex(dragBaseFrameRef.current - frameDelta, framesCount)
    handleFrameChange(newIndex)
  }, [pixelsPerFrame, framesCount, handleFrameChange])

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false
  }, [])

  // ── Drag — touch ──────────────────────────────────────────────────────────

  const handleTouchStart = useCallback((e) => {
    if (!isLoadedRef.current) return
    if (e.touches.length !== 1) return
    cancelAnimationFrame(animRafRef.current)
    isDraggingRef.current    = true
    dragStartXRef.current    = e.touches[0].clientX
    dragBaseFrameRef.current = currentIndexRef.current
  }, [])

  const handleTouchMove = useCallback((e) => {
    if (!isDraggingRef.current || e.touches.length !== 1) return
    const deltaX     = e.touches[0].clientX - dragStartXRef.current
    const frameDelta = Math.round(deltaX / pixelsPerFrame)
    const newIndex   = wrapFrameIndex(dragBaseFrameRef.current - frameDelta, framesCount)
    handleFrameChange(newIndex)
  }, [pixelsPerFrame, framesCount, handleFrameChange])

  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false
  }, [])

  // ── Button controls ───────────────────────────────────────────────────────

  const handleRotateLeft = useCallback(() => {
    handleFrameChange(wrapFrameIndex(currentIndexRef.current - 1, framesCount))
  }, [framesCount, handleFrameChange])

  const handleRotateRight = useCallback(() => {
    handleFrameChange(wrapFrameIndex(currentIndexRef.current + 1, framesCount))
  }, [framesCount, handleFrameChange])

  // ── Animated go-to-north (shortest path) ─────────────────────────────────

  const animRafRef = useRef(null)

  useEffect(() => () => cancelAnimationFrame(animRafRef.current), [])

  const handleGoToNorth = useCallback(() => {
    cancelAnimationFrame(animRafRef.current)

    const northFrame      = axonometricNorthFrameIndex(framesCount)
    const attractiveFrame = attractiveFrameRef?.current ?? northFrame

    // Toggle between the two meaningful views: geographic north and the
    // attractive (balcony-facing) frame. When already at north → go to
    // attractive; otherwise → go to north.
    const target = currentIndexRef.current === northFrame ? attractiveFrame : northFrame

    if (currentIndexRef.current === target) return

    const { step } = shortestRotationPath(currentIndexRef.current, target, framesCount)

    let lastTs = 0
    const loop = (now) => {
      if (now - lastTs >= ANIM_INTERVAL_MS) {
        const next = wrapFrameIndex(currentIndexRef.current + step, framesCount)
        handleFrameChange(next)
        lastTs = now
        if (next === target) return
      }
      animRafRef.current = requestAnimationFrame(loop)
    }
    animRafRef.current = requestAnimationFrame(loop)
  }, [framesCount, handleFrameChange])

  // ── Intro animation — fires once per session per axonometric mode ────────
  //
  // Starts at the attractive frame (or northFrame as fallback), then completes
  // one full 360° loop — the user sees the whole model before landing back
  // at the best view. Controlled by the shared bim:onboarding_seen:axonometric
  // key: '1' = already dismissed overlay → skip; anything else → play loop.

  const introFiredRef = useRef(false)

  useEffect(() => {
    if (!isLoaded) return
    if (introFiredRef.current) return

    const northFrame  = axonometricNorthFrameIndex(framesCount)

    // Use the SVG-based attractive frame when available; fall back to the
    // geographic-north frame so the intro always lands at a meaningful view.
    const introTarget = attractiveFrameRef?.current ?? northFrame

    // Share the seen-flag with the OnboardingOverlay: when the user has already
    // dismissed the onboarding for axonometric mode ('1'), skip the intro and
    // snap to the best view.  The key is written by OnboardingContext (not here).
    if (sessionStorage.getItem(onboardingSessionKey(PRODUCT_VIEW_MODE.AXONOMETRIC)) === '1') {
      introFiredRef.current = true
      // No intro on revisit — snap directly to introTarget.
      handleFrameChange(introTarget)
      return
    }

    introFiredRef.current = true

    // Full 360° loop starting at introTarget: snap to the attractive/north
    // frame first, then advance through all framesCount steps, landing back
    // at introTarget. With framesCount=8 at 200ms/step: 8 × 200 ms = 1.6 s.
    handleFrameChange(introTarget)

    let stepsRemaining = framesCount
    let lastTs = 0
    const loop = (now) => {
      if (now - lastTs >= INTRO_FRAME_MS) {
        const next = wrapFrameIndex(currentIndexRef.current + 1, framesCount)
        handleFrameChange(next)
        lastTs = now
        stepsRemaining -= 1
        if (stepsRemaining <= 0) return
      }
      animRafRef.current = requestAnimationFrame(loop)
    }
    animRafRef.current = requestAnimationFrame(loop)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded])

  // ── Return ────────────────────────────────────────────────────────────────

  return {
    frameImgRefsRef,
    currentIndexRef,
    isDraggingRef,
    getFrameUrl,
    loadingProgress,
    isLoaded,
    compassAzimuthDeg,
    onImgLoad,
    handleFrameChange,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleRotateLeft,
    handleRotateRight,
    handleGoToNorth,
  }
}
